<template>
    <div class="settings-wrapper">
        <button class="settings-trigger" @click="isOpen = true" aria-label="Open settings">
            <i class="fa fa-cog"></i>
        </button>

        <transition name="drawer">
            <div v-if="isOpen" class="drawer-overlay" @click.self="close">
                <div class="drawer-panel">
                    <div class="drawer-header">
                        <span>Settings</span>
                        <button class="drawer-close" @click="close"><i class="fa fa-times"></i></button>
                    </div>
                    <div class="drawer-body">
                        <!-- Theme -->
                        <div class="setting-group">
                            <label class="setting-label">Theme</label>
                            <div class="theme-switcher">
                                <button :class="['theme-btn', { active: theme === 'light' }]" @click="setTheme('light')">
                                    <i class="fa fa-sun-o"></i> Light
                                </button>
                                <button :class="['theme-btn', { active: theme === 'dark' }]" @click="setTheme('dark')">
                                    <i class="fa fa-moon-o"></i> Dark
                                </button>
                                <button :class="['theme-btn', { active: theme === 'auto' }]" @click="setTheme('auto')">
                                    <i class="fa fa-magic"></i> Auto
                                </button>
                            </div>
                        </div>

                        <!-- Sound -->
                        <div class="setting-group">
                            <label class="setting-label">Sound</label>
                            <div class="setting-row">
                                <span class="setting-desc">Celebration sound</span>
                                <label class="switch">
                                    <input type="checkbox" v-model="soundEnabled" @change="saveSoundPref" />
                                    <span class="slider"></span>
                                </label>
                            </div>
                            <div class="setting-row" v-if="soundEnabled">
                                <span class="setting-desc">Sound type</span>
                                <select v-model="soundType" @change="saveSoundPref" class="mini-select">
                                    <option value="chime">Chime</option>
                                    <option value="fanfare">Fanfare</option>
                                    <option value="bell">Bell</option>
                                    <option value="pop">Pop</option>
                                    <option value="clap">Clap 👏</option>
                                </select>
                            </div>
                            <button v-if="soundEnabled" class="preview-btn" @click="previewSound">
                                <i class="fa fa-volume-up"></i> Preview
                            </button>
                        </div>

                        <!-- Deck -->
                        <div class="setting-group">
                            <label class="setting-label">Card Deck</label>
                            <select @change="changeDeck($event)" class="deck-select" :value="selectedDeck">
                                <option v-for="(item, index) in availableDecks" v-bind:key="index" v-bind:value="index">
                                    {{ item.name }} ({{ item.cards.join(', ') }})
                                </option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script lang="ts">
import Vue from 'vue'

export default Vue.extend({
    name: 'roomSettings',
    data() {
        return {
            isOpen: false,
            theme: localStorage.getItem('pp-theme') || 'light',
            soundEnabled: localStorage.getItem('pp-sound') !== 'false',
            soundType: localStorage.getItem('pp-sound-type') || 'chime',
        }
    },
    created() {
        this.applyTheme()
        // Listen for system theme changes when in auto mode
        window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
            if (this.theme === 'auto') this.applyTheme()
        })
    },
    methods: {
        close() {
            this.isOpen = false
        },
        changeDeck(event: any) {
            this.$socket.emit('changeDeck', parseInt(event.target.value))
        },
        setTheme(t: string) {
            this.theme = t
            localStorage.setItem('pp-theme', t)
            this.applyTheme()
        },
        applyTheme() {
            let effective = this.theme
            if (effective === 'auto') {
                effective = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
            }
            document.documentElement.setAttribute('data-theme', effective)
        },
        saveSoundPref() {
            localStorage.setItem('pp-sound', String(this.soundEnabled))
            localStorage.setItem('pp-sound-type', this.soundType)
        },
        previewSound() {
            this.playSound(this.soundType)
        },
        playSound(type: string) {
            try {
                const ctx = new (window.AudioContext || (window as any).webkitAudioContext)()
                if (type === 'chime') {
                    const notes = [523.25, 659.25, 783.99, 1046.50]
                    notes.forEach((freq, i) => {
                        const osc = ctx.createOscillator()
                        const gain = ctx.createGain()
                        osc.connect(gain); gain.connect(ctx.destination)
                        osc.type = 'sine'; osc.frequency.value = freq
                        gain.gain.setValueAtTime(0.25, ctx.currentTime + i * 0.12)
                        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + i * 0.12 + 0.35)
                        osc.start(ctx.currentTime + i * 0.12)
                        osc.stop(ctx.currentTime + i * 0.12 + 0.35)
                    })
                } else if (type === 'fanfare') {
                    const notes = [392, 523.25, 659.25, 783.99, 1046.50]
                    notes.forEach((freq, i) => {
                        const osc = ctx.createOscillator()
                        const gain = ctx.createGain()
                        osc.connect(gain); gain.connect(ctx.destination)
                        osc.type = 'triangle'; osc.frequency.value = freq
                        gain.gain.setValueAtTime(0.3, ctx.currentTime + i * 0.1)
                        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + i * 0.1 + 0.5)
                        osc.start(ctx.currentTime + i * 0.1)
                        osc.stop(ctx.currentTime + i * 0.1 + 0.5)
                    })
                } else if (type === 'bell') {
                    const osc = ctx.createOscillator()
                    const gain = ctx.createGain()
                    osc.connect(gain); gain.connect(ctx.destination)
                    osc.type = 'sine'; osc.frequency.value = 830
                    gain.gain.setValueAtTime(0.4, ctx.currentTime)
                    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 1.0)
                    osc.start(ctx.currentTime)
                    osc.stop(ctx.currentTime + 1.0)
                } else if (type === 'pop') {
                    const osc = ctx.createOscillator()
                    const gain = ctx.createGain()
                    osc.connect(gain); gain.connect(ctx.destination)
                    osc.type = 'square'
                    osc.frequency.setValueAtTime(600, ctx.currentTime)
                    osc.frequency.exponentialRampToValueAtTime(100, ctx.currentTime + 0.1)
                    gain.gain.setValueAtTime(0.3, ctx.currentTime)
                    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15)
                    osc.start(ctx.currentTime)
                    osc.stop(ctx.currentTime + 0.15)
                } else if (type === 'clap') {
                    for (let i = 0; i < 5; i++) {
                        const bufSize = 4096
                        const buf = ctx.createBuffer(1, bufSize, ctx.sampleRate)
                        const data = buf.getChannelData(0)
                        for (let j = 0; j < bufSize; j++) { data[j] = Math.random() * 2 - 1 }
                        const noise = ctx.createBufferSource()
                        noise.buffer = buf
                        const bpf = ctx.createBiquadFilter()
                        bpf.type = 'bandpass'
                        bpf.frequency.value = 1200 + i * 200
                        bpf.Q.value = 1.5
                        const gain = ctx.createGain()
                        noise.connect(bpf); bpf.connect(gain); gain.connect(ctx.destination)
                        const t = ctx.currentTime + i * 0.12
                        gain.gain.setValueAtTime(0.35, t)
                        gain.gain.exponentialRampToValueAtTime(0.01, t + 0.08)
                        noise.start(t)
                        noise.stop(t + 0.08)
                    }
                }
            } catch (e) { /* noop */ }
        }
    },
    computed: {
        availableDecks() {
            return this.$store.getters.availableDecks
        },
        selectedDeck() {
            return this.$store.getters.room?.selectedDeck ?? 0
        }
    },
})
</script>

<style scoped>
.settings-wrapper {
    position: relative;
}

.settings-trigger {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    border: 1.5px solid var(--border-color);
    background: var(--card-bg);
    color: var(--text-secondary);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1em;
    transition: all 0.25s;
    box-shadow: var(--shadow-sm);
}

.settings-trigger:hover {
    background: var(--card-hover);
    color: var(--text-primary);
    transform: rotate(45deg);
    box-shadow: var(--shadow-md);
}

.drawer-overlay {
    position: fixed;
    top: 0; right: 0; bottom: 0; left: 0;
    background: rgba(0,0,0,0.4);
    backdrop-filter: blur(4px);
    z-index: 9999;
    display: flex;
    justify-content: flex-end;
}

.drawer-panel {
    width: 300px;
    max-width: 85vw;
    height: 100vh;
    background: var(--panel-bg);
    border-left: 1px solid var(--border-color);
    display: flex;
    flex-direction: column;
    animation: slideIn 0.3s cubic-bezier(0.16, 1, 0.3, 1);
    box-shadow: -8px 0 30px rgba(0,0,0,0.15);
    overflow: hidden;
}

@keyframes slideIn {
    from { transform: translateX(100%); }
    to { transform: translateX(0); }
}

.drawer-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 20px 16px;
    font-size: 1.1em;
    font-weight: 700;
    color: var(--text-primary);
    border-bottom: 1px solid var(--border-color);
}

.drawer-close {
    background: var(--card-bg);
    border: 1px solid var(--border-color);
    color: var(--text-secondary);
    cursor: pointer;
    font-size: 0.9em;
    padding: 6px 10px;
    border-radius: 8px;
    transition: all 0.2s;
}

.drawer-close:hover {
    color: var(--text-primary);
    background: var(--card-hover);
}

.drawer-body {
    padding: 20px;
    overflow-y: auto;
    flex: 1;
}

.setting-group {
    margin-bottom: 28px;
}

.setting-label {
    display: block;
    font-size: 0.68em;
    text-transform: uppercase;
    letter-spacing: 1.5px;
    color: var(--text-tertiary);
    margin-bottom: 12px;
    font-weight: 700;
}

.theme-switcher {
    display: flex;
    gap: 6px;
}

.theme-btn {
    flex: 1;
    padding: 10px 6px;
    border-radius: 10px;
    border: 1.5px solid var(--border-color);
    background: var(--card-bg);
    color: var(--text-secondary);
    cursor: pointer;
    font-size: 0.78em;
    font-weight: 500;
    transition: all 0.2s;
    text-align: center;
    box-shadow: var(--shadow-sm);
}

.theme-btn:hover {
    background: var(--card-hover);
}

.theme-btn.active {
    border-color: var(--accent);
    background: var(--accent-subtle);
    color: var(--accent);
    font-weight: 600;
    box-shadow: 0 2px 8px var(--accent-glow);
}

.setting-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 0;
}

.setting-desc {
    font-size: 0.82em;
    color: var(--text-secondary);
}

/* Toggle switch */
.switch {
    position: relative;
    width: 40px;
    height: 22px;
}

.switch input {
    opacity: 0;
    width: 0;
    height: 0;
}

.slider {
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background: var(--border-color);
    border-radius: 11px;
    cursor: pointer;
    transition: 0.3s;
}

.slider:before {
    content: '';
    position: absolute;
    height: 16px;
    width: 16px;
    left: 3px;
    bottom: 3px;
    background: var(--panel-bg);
    border-radius: 50%;
    transition: 0.3s;
    box-shadow: 0 1px 3px rgba(0,0,0,0.2);
}

.switch input:checked + .slider {
    background: var(--accent);
}

.switch input:checked + .slider:before {
    transform: translateX(18px);
    background: white;
}

.mini-select {
    padding: 6px 12px;
    border-radius: 8px;
    border: 1px solid var(--border-color);
    background: var(--card-bg);
    color: var(--text-primary);
    font-size: 0.8em;
    outline: none;
    box-shadow: var(--shadow-sm);
}

.mini-select option {
    background: var(--panel-bg);
    color: var(--text-primary);
}

.preview-btn {
    margin-top: 10px;
    padding: 7px 14px;
    border-radius: 8px;
    border: 1px solid var(--border-color);
    background: var(--card-bg);
    color: var(--text-secondary);
    cursor: pointer;
    font-size: 0.78em;
    transition: all 0.2s;
    box-shadow: var(--shadow-sm);
}

.preview-btn:hover {
    background: var(--card-hover);
    color: var(--text-primary);
    box-shadow: var(--shadow-md);
}

.deck-select {
    width: 100%;
    padding: 10px 14px;
    border-radius: 10px;
    border: 1.5px solid var(--border-color);
    background: var(--card-bg);
    color: var(--text-primary);
    font-size: 0.85em;
    outline: none;
    box-shadow: var(--shadow-sm);
}

.deck-select option {
    background: var(--panel-bg);
    color: var(--text-primary);
}

.drawer-enter-active, .drawer-leave-active {
    transition: opacity 0.25s;
}
.drawer-enter, .drawer-leave-to {
    opacity: 0;
}
</style>